import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	IStyleSettingsCarouselUploaderContext,
	StyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { useTranslation } from 'react-i18next';
import { openBrowseFile } from 'utils/openBrowseFile';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { Radio } from 'components/Radio/Radio';
import Text from 'components/Text/Text';
import 'react-image-crop/dist/ReactCrop.css';

type TImageOrientation = 'horizontal' | 'vertical';

interface IProps {
	titleKey: string;
	orientationRadioName: string;
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxFileSizeMB: number;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	onUploadItem: ({ sourceUrl, fileId }: { sourceUrl: string; fileId: string }) => void;
}

export const CropperModalNew: React.FC<IProps> = ({
	titleKey,
	orientationRadioName,
	horizontalAspectRatio,
	verticalAspectRatio,
	maxFileSizeMB,
	showGroupLoader,
	hideGroupLoader,
	onUploadItem,
}) => {
	const { t } = useTranslation('form');
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setCropperSrc,
		uploadImage,
		cropperSrc,
		typeOfModalWindow,
	} = useContext<IStyleSettingsCarouselUploaderContext>(StyleSettingsCarouselUploaderContext);
	const [imageOrientation, setImageOrientation] = useState<TImageOrientation>('horizontal');
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const [isCropAreaChanged, setCropAreaChanged] = useState<boolean>(false);
	const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
	const canvasRef = useRef<Nullable<HTMLCanvasElement>>(null);
	const imageRef = useRef<Nullable<HTMLImageElement>>(null);
	const [allowFileUpload, setAllowFileUpload] = useState<boolean>(true);
	const [imageFile, setImageFile] = useState<Nullable<File>>(null);
	const [dataToUpload, setDataToUpload] = useState<{
		status: 'success' | 'error';
		sourceUrl?: Nullable<string>;
		fileId?: Nullable<string>;
	}>();

	const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

	useEffect(() => {
		if (dataToUpload?.status === 'success' && dataToUpload.sourceUrl && dataToUpload.fileId) {
			onUploadItem({ sourceUrl: dataToUpload.sourceUrl, fileId: dataToUpload.fileId });
			hideGroupLoader();
		} else if (dataToUpload?.status === 'error') {
			hideGroupLoader();
			changeTypeOfErrorModalWindow('fileUploadError');
		}
		setDataToUpload(undefined);
		setAllowFileUpload(true);
	}, [dataToUpload]);

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setCrop(undefined);
		setCropAreaChanged(false);
		setCropperSrc(null);
		setImageFile(null);
	};

	const handleOrientationChange = (value: TImageOrientation) => {
		setImageOrientation(value);
		setCrop(undefined);
		setSentOriginalImg(true);
	};

	const handleImageChange = (files: Nullable<FileList>) => {
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = files?.[0];

		if (!imgFile) {
			return;
		}

		if (imgFile.size > 1000 * 1000 * maxFileSizeMB) {
			changeTypeOfErrorModalWindow('imageFileSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			setCropperSrc(URL.createObjectURL(imgFile));
			setImageFile(imgFile);
		} else {
			changeTypeOfErrorModalWindow('invalidFileFormat');
		}
	};

	const handleUploadImage = () => {
		openBrowseFile({ onChoose: handleImageChange, accept: '.jpg, .jpeg, .png' });
	};

	const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (canvasRef.current && imageRef.current) {
			canvasRef.current.width = imageRef.current.naturalWidth;
			canvasRef.current.height = imageRef.current.naturalHeight;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
		}
	};

	const handleCropAreaChange = (c: PixelCrop) => {
		setCrop(c);
		isSentOriginalImg && setSentOriginalImg(false);
		if (canvasRef.current && imageRef.current) {
			setCropAreaChanged(true);
			canvasRef.current.width = c.width;
			canvasRef.current.height = c.height;
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(
				imageRef.current,
				c.x * scaleX,
				c.y * scaleY,
				c.width * scaleX,
				c.height * scaleY,
				0,
				0,
				c.width,
				c.height,
			);
		}
	};

	const uploadOriginalImage = async (file: File) => {
		const sourceUrl = URL.createObjectURL(file);
		const fileId = await uploadImage(file);
		fileId ? setDataToUpload({ status: 'success', sourceUrl, fileId }) : setDataToUpload({ status: 'error' });
	};

	const handleApplyImageSize = () => {
		if (canvasRef.current?.width === 0 && canvasRef.current.height === 0 && imageFile) {
			uploadOriginalImage(imageFile);
		} else if (isSentOriginalImg && imageFile) {
			uploadOriginalImage(imageFile);
		} else {
			canvasRef.current?.toBlob(
				async (blob) => {
					if (blob) {
						const sourceUrl = URL.createObjectURL(blob);
						const fileId = await uploadImage(blob);
						fileId
							? setDataToUpload({ status: 'success', sourceUrl, fileId })
							: setDataToUpload({ status: 'error' });
					}
				},
				'image/jpeg',
				0.8,
			);
		}
	};

	const handleApply = () => setAllowFileUpload(false);

	useEffect(() => {
		if (!allowFileUpload) {
			closeModal();
			showGroupLoader();
			handleApplyImageSize();
		}
	}, [allowFileUpload]);

	return (
		<ModalWrapper isVisible={typeOfModalWindow === 'cropper'}>
			<div className="absolute inset-0 p-4 flex items-center justify-center">
				<div
					className="w-full max-w-5xl p-8 rounded flex flex-col gap-4 bg-white-00-new shadow-500"
					data-test="CropperModal">
					<div className="flex items-center gap-4">
						<Text
							className="flex-1"
							translationNameSpace="mediaSettings"
							translationKey={titleKey}
							size="l"
							weight="bold"
						/>
						<NewButton
							variant="ghost"
							color="gray"
							size="small"
							icon={true}
							iconLeft={<Icon name={IconName.close} size="lg" />}
							ariaLabel={t('closeButton')}
							testId="closeCropperButton"
							onClick={closeModal}
						/>
					</div>
					{cropperSrc ? (
						<div className="flex justify-center">
							<ReactCrop
								className="max-h-96"
								crop={crop}
								aspect={aspectRatio}
								onChange={handleCropAreaChange}>
								<img ref={imageRef} src={cropperSrc} onLoad={drawImageInCanvas} alt="Photo" />
							</ReactCrop>
						</div>
					) : (
						<div className="h-96 rounded flex items-center justify-center bg-purple-01-new">
							<Icon name={IconName.image} size="smallHero" className="text-purple-03-new" />
						</div>
					)}
					<div className="flex items-center gap-4">
						<NewButton
							size="small"
							iconLeft={<Icon name={IconName.upload} size="lg" />}
							testId="chooseImageButton"
							onClick={handleUploadImage}>
							<Text translationNameSpace="form" translationKey="uploadImageButton" asFragment={true} />
						</NewButton>
						<div className="flex-1 flex justify-end gap-4">
							<Radio
								checked={imageOrientation === 'horizontal'}
								name={orientationRadioName}
								testId="horizontalRatio"
								onChange={() => handleOrientationChange('horizontal')}>
								<Text translationNameSpace="form" translationKey="horizontalCropLabel" />
							</Radio>
							<Radio
								checked={imageOrientation === 'vertical'}
								name={orientationRadioName}
								testId="verticalRatio"
								onChange={() => handleOrientationChange('vertical')}>
								<Text translationNameSpace="form" translationKey="verticalCropLabel" />
							</Radio>
						</div>
					</div>
					<div className="flex justify-end gap-4">
						<NewButton variant="outline" color="gray" testId="cancelCropperButton" onClick={closeModal}>
							<Text translationNameSpace="form" translationKey="cancelButton" asFragment={true} />
						</NewButton>
						<NewButton
							disabled={!cropperSrc || !isCropAreaChanged || !allowFileUpload}
							testId="applyButton"
							onClick={handleApply}>
							<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
						</NewButton>
					</div>
				</div>
			</div>
			<canvas className="hidden" ref={canvasRef} />
		</ModalWrapper>
	);
};
